import { Printer, DownloadSimple } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { formatMoney, formatBusinessDate, formatTime, denominationByCode } from "./utils";
import type { DayCloseLabels, ZReportPayload } from "./types";

export interface ZReportPreviewProps {
  payload: ZReportPayload;
  labels: DayCloseLabels;
  locale: "tr" | "en";
  onPrint?: () => void;
  onDownload?: () => void;
  className?: string;
}

export function ZReportPreview({
  payload, labels, locale, onPrint, onDownload, className,
}: ZReportPreviewProps) {
  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-biz-foreground font-display">
          {labels.steps.submit.zReport}
        </h3>
        <div className="flex items-center gap-2 print:hidden">
          {onPrint && (
            <button
              type="button"
              onClick={onPrint}
              className="flex items-center gap-1.5 h-10 px-3 rounded-biz-btn border border-biz-border bg-biz-surface-elevated hover:bg-biz-muted text-sm font-semibold biz-focus-ring text-biz-foreground"
            >
              <Printer className="w-4 h-4" />
              {labels.steps.submit.print}
            </button>
          )}
          {onDownload && (
            <button
              type="button"
              onClick={onDownload}
              className="flex items-center gap-1.5 h-10 px-3 rounded-biz-btn border border-biz-border bg-biz-surface-elevated hover:bg-biz-muted text-sm font-semibold biz-focus-ring text-biz-foreground"
            >
              <DownloadSimple className="w-4 h-4" />
              {labels.steps.submit.download}
            </button>
          )}
        </div>
      </div>

      <article
        className="z-report mx-auto max-w-[360px] bg-white text-black p-5 rounded-biz-card border border-biz-border biz-shadow-sm font-mono-num text-[11px] leading-snug print:shadow-none print:border-0 print:max-w-none"
        aria-label={labels.steps.submit.zReport}
      >
        <header className="text-center space-y-0.5 pb-2 border-b border-dashed border-black/30">
          <div className="font-bold text-sm tracking-wider">PAPTOQ BUSINESS</div>
          <div>{payload.branchName}</div>
          <div>{labels.steps.submit.zReport}</div>
          <div>{formatBusinessDate(payload.businessDate, locale)}</div>
        </header>

        <section className="py-2 space-y-0.5 border-b border-dashed border-black/30">
          <Row k={labels.zReportLabels.closedBy} v={payload.closedByName} />
          <Row k={labels.zReportLabels.closedAt} v={formatTime(payload.closedAt, locale)} />
        </section>

        <section className="py-2 space-y-0.5 border-b border-dashed border-black/30">
          <Row k={labels.zReportLabels.gross}    v={formatMoney(payload.grossMinor, labels.currencyPrefix, locale)} />
          <Row k={labels.zReportLabels.discount} v={"-" + formatMoney(payload.discountMinor, labels.currencyPrefix, locale)} />
          <Row k={labels.zReportLabels.tax}      v={formatMoney(payload.taxMinor, labels.currencyPrefix, locale)} />
          <Row k={labels.zReportLabels.refund}   v={"-" + formatMoney(payload.refundMinor, labels.currencyPrefix, locale)} />
          <Row k={labels.zReportLabels.voided}   v={"-" + formatMoney(payload.voidedMinor, labels.currencyPrefix, locale)} />
          <Row k={labels.zReportLabels.net}      v={formatMoney(payload.netMinor, labels.currencyPrefix, locale)} bold />
        </section>

        <section className="py-2 border-b border-dashed border-black/30">
          <div className="font-bold mb-1">{labels.zReportLabels.tenders}</div>
          {payload.tenders.map((t) => (
            <Row key={t.method}
              k={`${(labels.steps.non_cash.methods[t.method as keyof typeof labels.steps.non_cash.methods]) ?? t.method} (${t.count})`}
              v={formatMoney(t.amountMinor, labels.currencyPrefix, locale)}
            />
          ))}
        </section>

        <section className="py-2 border-b border-dashed border-black/30">
          <div className="font-bold mb-1">{labels.zReportLabels.categories}</div>
          {payload.categories.map((c) => (
            <Row key={c.key} k={c.label} v={formatMoney(c.amountMinor, labels.currencyPrefix, locale)} />
          ))}
        </section>

        <section className="py-2">
          <div className="font-bold mb-1">{labels.zReportLabels.denominations}</div>
          {payload.denominations.filter((d) => d.count > 0).map((d) => {
            const denom = denominationByCode(d.code);
            return (
              <Row
                key={d.code}
                k={`${labels.denominations[d.code] ?? denom.label} × ${d.count}`}
                v={formatMoney(denom.unitMinor * d.count, labels.currencyPrefix, locale)}
              />
            );
          })}
        </section>

        {payload.override && (
          <section className="py-2 border-t border-dashed border-black/30">
            <div className="font-bold mb-1">{labels.zReportLabels.override}</div>
            <Row k="Manager" v={payload.override.managerName ?? "-"} />
            <Row k="Reason" v={payload.override.reasonCode ?? "-"} />
            {payload.override.explanation && (
              <p className="mt-1">{payload.override.explanation}</p>
            )}
          </section>
        )}

        <footer className="text-center text-[10px] pt-2 border-t border-dashed border-black/30">
          *** END OF DAY ***
        </footer>
      </article>

      <style>{`
        @media print {
          body * { visibility: hidden; }
          .z-report, .z-report * { visibility: visible; }
          .z-report { position: absolute; left: 0; top: 0; width: 80mm; max-width: 80mm; }
        }
      `}</style>
    </div>
  );
}

function Row({ k, v, bold }: { k: string; v: string; bold?: boolean }) {
  return (
    <div className={cn("flex justify-between gap-2", bold && "font-bold")}>
      <span className="truncate">{k}</span>
      <span className="tabular-nums">{v}</span>
    </div>
  );
}
